import { Button, Card, Grid, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconCheck, IconExternalLink, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import isInvalidCronExpression from '../../helpers/cronValidator';
import GoogleAddAssetsModal from '../Modals/GoogleAddAssetsModal';
import NextSyncLabel from '../NextSyncLabel';
import OptionsSelect from '../OptionsSelect';

const GoogleSettings = ({ googleAccount, handleChange, connectButton, merchantCenter }) => {
  const { domainId } = useParams();
  const [requiredAssetsModalOpen, setRequiredAssetsModalOpen] = useState(false);
  const [addAssetModalOpen, setAddAssetModalOpen] = useState(false);
  return (
    <Card withBorder>
      <GoogleAddAssetsModal
        isOpen={requiredAssetsModalOpen}
        close={() => setRequiredAssetsModalOpen(false)}
        requiredMode
      />
      <GoogleAddAssetsModal
        isOpen={addAssetModalOpen}
        close={() => setAddAssetModalOpen(false)}
      />
      <Card.Section inheritPadding py="sm">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Name
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput disabled name="name" type="text" value={googleAccount?.name || ''} />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Email
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput disabled name="email" type="text" value={googleAccount?.email || ''} />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              User sub
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput disabled name="sub" type="text" value={googleAccount?.sub || ''} />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Ad account*
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <OptionsSelect
              name="adAccount"
              endpoint="google/accessibleAccounts"
              handleChange={handleChange}
              text="The Google Ad Account where all campaigns will be created"
              value={googleAccount?.adAccountId}
            />
          </Grid.Col>
        </Grid>

        {googleAccount?.domainId?.toString() === domainId && (
          <>
            {googleAccount?.adAccountId && (
              <>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Assets*
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <Stack gap="xs">
                      <Button
                        fullWidth
                        onClick={() => setRequiredAssetsModalOpen(true)}
                        disabled={googleAccount?.requiredAssets}
                      >
                        {googleAccount?.requiredAssets ? (
                          <>
                            <IconCheck size={14} /> Required Assets are added
                          </>
                        ) : (
                          <>
                            <IconPlus size={14} /> Add Required Assets
                          </>
                        )}
                      </Button>
                      <Button fullWidth variant="light" onClick={() => setAddAssetModalOpen(true)}>
                        <IconPlus size={14} /> Add Asset to Library
                      </Button>
                    </Stack>
                    <Text size="sm" c="dimmed">
                      A Performance Max campaign requires an initial minimum set of assets. Additional assets can be
                      added any time and will appear in the asset library for campaign selection.
                    </Text>
                  </Grid.Col>
                </Grid>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Merchant Center (optional)
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 5 }}>
                    <OptionsSelect
                      name="merchant"
                      endpoint={`google/merchantCenterAccounts/${googleAccount.adAccountId}`}
                      handleChange={handleChange}
                      text="You can link a Merchant Center account to run new campaigns with its product information!"
                      value={googleAccount?.merchantId}
                      required={false}
                    />
                  </Grid.Col>
                  {googleAccount?.merchantId && <Grid.Col span={{ base: 12, sm: 3 }}>{connectButton}</Grid.Col>}
                </Grid>
              </>
            )}
            {merchantCenter?.linked && (
              <>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Sync Releva Products to Merchant Center
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <Switch
                      disabled={!googleAccount?.merchantId}
                      checked={googleAccount?.productSync}
                      onChange={(e) =>
                        handleChange({ target: { name: 'productSync', value: e.currentTarget.checked } })
                      }
                    />
                    {merchantCenter?.hasProducts ? (
                      googleAccount?.productSync ? (
                        <Text size="sm" c="blue">
                          Releva's product catalog will be synced with the selected Merchant Center account.
                        </Text>
                      ) : (
                        <Text size="sm" c="blue">
                          The selected Merchant account already contains products that will be used in the created
                          Google Ads campaigns.
                        </Text>
                      )
                    ) : (
                      <Text size="sm" c="red">
                        There are no products in the Merchant account. Please ENABLE product sync from the Releva
                        catalog or add products from your Merchant Center!
                      </Text>
                    )}
                  </Grid.Col>
                </Grid>
                {googleAccount?.productSync && (
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <Text size="sm" fw={500}>
                        Catalog Sync Schedule
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        name="productSyncSchedule"
                        type="text"
                        placeholder="0 7 * * *"
                        required
                        value={googleAccount?.productSyncSchedule || ''}
                        error={isInvalidCronExpression(googleAccount?.productSyncSchedule)}
                        onChange={handleChange}
                      />
                      <Text size="sm" c="dimmed">
                        This{' '}
                        <a href="https://crontab.guru/" rel="noreferrer" target="_blank">
                          cron expression <IconExternalLink size={14} />
                        </a>{' '}
                        controls how often we sync the product catalog from Releva to selected Merchant Center account.
                        It will be evaluated under the UTC timezone.
                      </Text>

                      <NextSyncLabel schedule={googleAccount?.productSyncSchedule} />
                    </Grid.Col>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Card.Section>
    </Card>
  );
};

export default GoogleSettings;
